import { useRef, useEffect } from 'react';
import {
  type UseCartMutations,
  convertPairingToCartPairing,
  convertOrderIngredientToCartProduct,
} from '@hungryroot/cart';
import { AccountStatus } from '@hungryroot/customer';
import { type DietaryRestrictionsText } from '@hungryroot/dietary-restrictions';
import { type PairingV1 as Pairing } from '@hungryroot/rest';
import {
  type PairingWithIngredientDetails,
  type UseEditRecipeResult,
} from '../../../../hooks/useEditRecipe/useEditRecipe';
import { useEditRecipeMutations } from '../../../edit-recipe/hooks/useEditRecipeMutations/useEditRecipeMutations';

interface UseRDPModalCartProps {
  cartMutations: UseCartMutations | null;
  accountStatus?: AccountStatus;
  currentPairing: PairingWithIngredientDetails;
  dietaryIngredients: DietaryRestrictionsText[];
  editRecipeQuery: UseEditRecipeResult;
  onDismiss: () => void;
}

export const useRDPModalCart = ({
  cartMutations,
  accountStatus,
  currentPairing,
  dietaryIngredients,
  editRecipeQuery,
  onDismiss,
}: UseRDPModalCartProps) => {
  const updateCartCallback = useRef<
    null | ((cartMutations: UseCartMutations | null) => void)
  >(null);
  const { createBundle } = useEditRecipeMutations();

  // the account status is still in a skipped/canceled/paused state when the user confirms
  // in the update cart modal, so we need to check the account status as a side effect to ensure
  // the action the user is taking reflects their most up-to-date account status
  useEffect(() => {
    if (
      updateCartCallback &&
      accountStatus === AccountStatus.Active &&
      cartMutations !== null
    ) {
      updateCartCallback.current?.(cartMutations);
      updateCartCallback.current = null;
    }
  }, [updateCartCallback, cartMutations, accountStatus]);

  const onAddClick = (cartMutations: UseCartMutations | null) => {
    if (editRecipeQuery.mutations.length > 0) {
      createBundle({
        modifications: editRecipeQuery.mutations,
        pairing: convertPairingToCartPairing(currentPairing as Pairing),
        pairingIngredients: currentPairing.ingredients.map((ingredient) => {
          return convertOrderIngredientToCartProduct(ingredient);
        }),
      });
    } else {
      cartMutations?.addPairing({
        pairing: currentPairing as Pairing,
        dietaryProductIngredients: dietaryIngredients,
        location: 'rdp',
      });
    }
  };

  const onRemoveClick = (cartMutations: UseCartMutations | null) => {
    cartMutations?.removePairing({
      pairing: currentPairing,
      location: 'rdp',
    });
    onDismiss();
  };

  const onSubtractClick = (cartMutations: UseCartMutations | null) => {
    cartMutations?.removePairing({
      pairing: currentPairing,
      location: 'rdp',
    });
  };

  return {
    updateCartCallback,
    onAddClick,
    onRemoveClick,
    onSubtractClick,
  };
};

export type RDPModalCart = ReturnType<typeof useRDPModalCart>;
